import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormControl } from '@angular/forms'
import { cloneDeep } from 'lodash';

// Services
import { CaseService } from '../_services/case.service';
import { QuestionService } from '../_services/question.service';
import { FormatService } from '../_services/format.service';
import { OrderByPipe } from 'ngx-pipes';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-survey',
  templateUrl: './survey.component.html',
  styleUrls: ['./survey.component.scss'],
  providers: [ CaseService, QuestionService, FormatService, OrderByPipe, DatePipe ]
})
export class SurveyComponent implements OnInit {

  caseCode: string;
  serviceItemId: number;
  accessible: boolean = true;
  peerGroup: number;
  survey;
  surveyQuestions;
  surveyQuestionsDefault;
  surveyForm: FormGroup;
  showSurvey: boolean = true;

  saving: boolean = false;
  error: string; success: string;

  // NHSE&I LD
  conditionalQuestionId: string = '22530'; //'22864';
  communityListId: number = 7128; //7481;
  hospitalListId: number = 7129; //7482;
  qualificationSelection: string;

  sessionCodes: any;
  incompleteResponses: boolean;
  caseCodeSplit: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private cases: CaseService,
    private questions: QuestionService,
    private format: FormatService,
    private order: OrderByPipe,
    private date: DatePipe
  ) { }

  ngOnInit() {
    // Set case code
    let caseCode = this.route.snapshot.paramMap.get('id');
    if (caseCode) {
      this.checkCaseCode(caseCode);
    }

    // Get session codes
    let sessionCodes: any = sessionStorage.getItem('sessionCodes');
    if (sessionCodes) {
      this.sessionCodes = JSON.parse(sessionCodes);
      if (!this.sessionCodes.includes(caseCode)) {
        this.sessionCodes.push(caseCode);
      }
    } else {
      this.sessionCodes = [];
      this.sessionCodes.push(caseCode);
    }
    sessionStorage.setItem('sessionCodes', JSON.stringify(this.sessionCodes));
  }

  private ensureSurveyTextDefaults(): void {
    if (!this.survey) {
      return;
    }

    if (!this.survey.submitInstructions) {
      this.survey.submitInstructions = 'To submit your response, please select the button below.';
    }
    if (!this.survey.submitButton) {
      this.survey.submitButton = 'Submit';
    }
    if (!this.survey.submitButtonLoading) {
      this.survey.submitButtonLoading = 'Submitting your responses...';
    }

    if (!this.survey.saveAndSubmitButton) {
      this.survey.saveAndSubmitButton = 'Save and submit all';
    }
    if (!this.survey.saveButtonLoading) {
      this.survey.saveButtonLoading = 'Saving your responses...';
    }
    if (!this.survey.saveStartNew) {
      this.survey.saveStartNew = 'Save and start new';
    }
  }

  getAllSurveys(caseCode: string) {
    this.cases.getSurveys().subscribe(
      success => {
        let surveys = [];
        
        success.data.projectSurveys.forEach(survey => {
          let formattedSurvey = this.format.mapSurveyData(survey)
          surveys.push(formattedSurvey);
        })

        // Reorder by surveyId
        // TODO: Add displaySequence to database
        surveys = this.order.transform(surveys, 'surveyId');
        
        this.getSurveySettings(surveys, caseCode);
      },
      error => {
        this.error = 'This link is no longer available.';
        console.log(error);
      }
    )
  }

  getSurveySettings(surveys: any, caseCode: string) {

    let surveyCount = surveys.length;
    let counter = 0;

    // Get survey settings
    // TODO: Add introShort and logoPath to /surveys end-point and remove need for loop
    surveys.forEach(survey => {
      this.cases.getSurveySettings(survey.surveyId).subscribe(
        success => {
          counter = counter + 1;
          success.data.surveySettings.forEach(setting => {
            let settingValue = setting.settingValue == 'true' || setting.settingValue == 'false' || setting.settingValue == 'null' ? JSON.parse(setting.settingValue) : setting.settingValue;
            survey[setting.settingName] = settingValue;
          });

          if (counter == surveyCount) {
            // Set all surveys and survey
            this.survey = surveys.filter(s => s.surveyCode === caseCode.split('-')[0].toUpperCase())[0];

            this.ensureSurveyTextDefaults();

            // Get questions
            if (this.survey.open) {
              this.getQuestionGroups(caseCode, this.survey);
            } else {
              this.error = "This survey is not currently live. It opens on " + this.date.transform(survey.openDt, 'dd-MM-yyyy') + ' and closes on ' + this.date.transform(survey.closeDt, 'dd-MM-yyyy') + '.'
            }
            
          }
        },
        error => {
          this.error = 'This link is no longer available.';
          console.log(error);
        }
      )
    });

  }

  checkCaseCode(caseCode) {
    this.cases.checkCaseCode(caseCode).subscribe(
      r => {
        let found = r.data.caseCodeFound;
        let submitted = r.data.dataSubmitted;
        let peerGroup = r.data.peerGroupId;

        // Set peer group
        if (peerGroup) {
          this.peerGroup = peerGroup;
        } else {
          this.peerGroup = 7; // TODO: Check this is correct thing to do
        }

        // Already submitted; re-route
        // if (submitted) {
        //   console.log(this.selectedLanguage);
        //   let translate: string;
        //   // if(this.selectedLanguage?.key) {
        //   //   this.selectedLanguage.key = 'english';
        //   //   translate = this.selectedLanguage.key;
        //   // } else {
        //   //   translate = 'english';
        //   // }
        //   this.router.navigate(['/complete'], { queryParams: { translated: this.selectedLanguage.key } });
        // }

        // Only show survey if found and not already submitted
        if (found) {
          this.accessible = true;

          // Set survey
          let survey = sessionStorage.getItem('survey');
          if (survey) { 
            this.survey = JSON.parse(survey);

            this.ensureSurveyTextDefaults();

            this.caseCodeSplit = caseCode.split('-');
            this.getQuestionGroups(caseCode, this.survey);
          } else {
            this.getAllSurveys(caseCode);
          }     
        } else {
          this.accessible = false;
        }

        // Set service item
        let services = r.data.submissionServices;
        if (services[0]) {
          this.serviceItemId = services[0].serviceItemId;
        } else {
          this.serviceItemId = 0;
        }
      },
      e => {
        this.error = 'Unable to find survey. Please try again or contact support.';
        console.log(e);
      }
    )
  }

  getQuestionGroups(caseCode, survey?) {

    this.caseCodeSplit = caseCode.split('-');

    // Set group level
    let surveyCodeLength = survey.surveyCode.length;
    let groupLevel = caseCode.substring(0, surveyCodeLength).replace('-', '').toUpperCase();
  
    this.questions.getQuestionGroups(caseCode, groupLevel).subscribe(
      r => {
        // Default to first question group
        let groups = r.data;
        if (groups.length && groups[0]) {
          this.getQuestions(caseCode, groups[0].groupId);
        } else {
          this.error = 'There are no questions set for this survey.'
        }
      },
      e => {
        this.error = 'There are no questions set for this survey.'
        console.log(e);
      }
    )
  }

  getQuestions(caseCode, groupId) {
    this.questions.getQuestions(caseCode, groupId, this.serviceItemId).subscribe(
      r => {

        // Set caseCode
        this.caseCode = caseCode;

        // Format questions
        this.surveyQuestions = this.formatQuestions(r.data.questions);
        if (!this.surveyQuestionsDefault) {
          this.surveyQuestionsDefault = cloneDeep(this.surveyQuestions)
        }

        // Add questions as controls to formGroup
        let questions = {};
        this.surveyQuestions.forEach(q => {
          if (q.response !== null) {

            // Fix date values
            let response;
            if (q.type == 'date') {
              response = q.response.substring(0, 10);
            } else {
              response = q.response;
            }

            // Set response value
            questions[q.id] = new FormControl(response);

          } else {
            questions[q.id] = new FormControl('');
          }
        });

        // Create form
        this.surveyForm = new FormGroup(questions);

        // Watch for changes/errors
        this.surveyChanges();

        // Hide if there's a qualification question
        if (this.survey.qualificationQuestion) {
          this.showSurvey = false;
        }

        // If qualification question selected
        let qualification = this.route.snapshot.queryParamMap.get('qualification');
        if (qualification) {
          this.changeQualification(+qualification);
        }


      },
      e => {
        this.error = e.error.error.message
        console.log(e);
      }
    )
  }

  // SAVE

  submit(next: string) {
    this.saving = true;
    let responses = [];

    // Create responses
    Object.keys(this.surveyForm.controls).forEach(c => {
      let response = this.surveyForm.controls[c], 
          question = this.surveyQuestions.find(q => q.id === +c);

      if (response.dirty) {
        responses.push({
          questionId: c,
          questionPart: question.part,
          questionType: question.type,
          value: response.value
        })
      }
    });

    // Format responses
    responses = this.formatResponses(responses);

    // Submit responses, if defined
    if (responses.length) {
      this.questions.submitResponses(this.caseCode, this.serviceItemId, responses).subscribe(
        r => {

          // Save responses without submitting
          if (this.survey.editable && next == 'remain') {
            this.success = "Your responses have been saved.";
            this.surveyForm.markAsPristine();
          }

          // Save and start new survey
          if (this.survey.editable && next == 'add') {
            this.newCode(this.caseCode);
          } 
          
          // Submit responses
          if (!this.survey.editable || next == 'complete') {
            this.questions.submitSurvey(this.caseCode).subscribe(
              r => {
                sessionStorage.removeItem('sessionCodes');                             
                this.router.navigate(['/complete']);
              },
              e => {
                console.log(e);
                this.error = 'Your survey has not been marked as completed. Please try submitting again.'
              }
            )
          }
        },
        e => {
          this.error = 'Your responses could not be saved. Please try again or contact support.';
          console.log(e);
        },
        () => {
          this.saving = false;
        }
      )
    }    

  }

  // UTILITIES

  formatQuestions(questions) {
    let output = [];

    questions.forEach(q => {
      let response = q.responses[0];
      let questionType;

      switch(q.questionType) {
        case 'NR':
          questionType = 'narrative'
          break;
        case 'TX':
          questionType = 'string'
          break;
        case 'N1':
          questionType = 'number'
          break;
        case 'LS':
          questionType = 'list'
          break;
        case 'RD':
          questionType = 'list'
          break;
        case 'YN':
          questionType = 'yn'
          break;
        case 'DT':
          questionType = 'date'
          break;
        default:
          questionType = 'narrative'
      }

      // Remove options with display sequence set to 100+ (hide)
      // TODO: Review this to include visibility setting
      let options = q.options.filter(o => o.displaySequence < 100);

      output.push({
        id:       q.questionId,
        part:     response ? response.questionPart : null,
        prefix:   q.questionPrefix,
        label:    q.questionText,
        type:     questionType,
        options:  options.map(o => { return { optionId: o.optionId, optionName: o.optionName, displaySequence: o.displaySequence } }),
        format:   q.formatModifier,
        help:     q.questionHelp,
        min:      q.rangeMin,
        max:      q.rangeMax,
        response: response ? response.value : null
      })
    });

    return output;
  }

  formatResponses(responses) {

    // Convert boolean true to 'Y'
    responses.forEach(r => {
      if (r.questionType === 'yn') {
        r.value = r.value === true ? 'Y' : false;
      }
    });

    // Remove boolean false (don't save to database)
    responses = responses.filter(r => r.value !== false);

    return responses;
  }

  surveyChanges(): void {
    this.surveyForm.valueChanges.subscribe(v => {
      Object.keys(v).forEach(key => {
        let question = this.surveyQuestions.filter(q => q.id === +key)[0];
        let errors = this.surveyForm.controls[key].errors;
        question.errors = errors;
      });
      this.incompleteCheck(this.surveyForm);
    });
  }

  changeQualification(listId) {

    // 1. CHANGE RESPONSE TO QUALIFICATION QUESTION
    // Change question response
    this.surveyForm.controls[this.conditionalQuestionId].setValue(listId)
    this.surveyForm.controls[this.conditionalQuestionId].markAsDirty();

    // Hide the question
    let match = this.surveyQuestions.find(sq => sq.id == this.conditionalQuestionId);
    match.hide = true;

    // Lock the qualification question
    this.qualificationSelection = listId;

    // Add to URL
    this.router.navigate([], { queryParams: { qualification: listId }, queryParamsHandling: 'merge' });

    // 2. SHOW ONLY THOSE QUESTIONS THAT APPLY TO PEER GROUP AND TYPE
    let hideQuestions;

    // Specialist and Community
    if (this.peerGroup === 5 && listId === this.communityListId) {
      //hideQuestions = ['22731', '22732', '22733', '22734', '22735', '22736', '22737', '22747', '22749', '22766', '22769'];
      hideQuestions = ['22554', '22572', '22575'];
    }
    // Specialist and Hospital
    if (this.peerGroup === 5 && listId === this.hospitalListId) {
      //hideQuestions = ['22731', '22732', '22733', '22734', '22735', '22736', '22737', '22747'];
      hideQuestions = [];
    }
    // Universal and Community
    if (this.peerGroup === 7 && listId === this.communityListId) {
      //hideQuestions = ['22749', '22764', '22765', '22766', '22767', '22768', '22769', '22770', '22771'];
      hideQuestions = ['22554', '22570', '22571', '22572', '22573', '22574', '22575', '22577']
    }
    // Universal and Hospital
    if (this.peerGroup === 7 && listId === this.hospitalListId) {
      //hideQuestions = ['22764', '22765', '22766', '22767', '22768', '22769', '22770', '22771'];
      hideQuestions = ['22570', '22571', '22572', '22573', '22574', '22575', '22577'];
    }

    hideQuestions.forEach(q => {
      // Find match in questions
      let match = this.surveyQuestions.find(sq => sq.id == q);
      // Hide and disable as double-indemnity
      if (match) {
        match.hide = true;
        this.surveyForm.controls[q].disable();
      }
    });

    // 3. SHOW FORM
    this.showSurvey = true;

  }

  toggleHelp(question) {
    let findQuestion = this.surveyQuestions.indexOf(question),
        thisQuestion = this.surveyQuestions[findQuestion];

    thisQuestion.showHelp = !thisQuestion.showHelp;
  }

  toggleCode(code, survey) {
    if (this.surveyForm.dirty) {
      if (window.confirm('You have unsaved changes on this form. If you continue, those changes will be lost. Click "OK" to continue, or "Cancel" to return to the page and save changes.')){
        this.getQuestionGroups(code, survey);
      }
    } else {
      this.getQuestionGroups(code, survey);
    }
  }

  newCode(code) {
    let codeParts = code.split('-');
    this.router.navigate(['/' + codeParts[0] + '/' + codeParts[2] + '-' + codeParts[1]], { queryParams: { bypassWelcome: true } })
  }

  incompleteCheck(form, check?: boolean) {
    let incomplete = [];

    // Get question IDs for complete
    Object.keys(form.value).forEach(v => {
      if (form.value[v] == "") {
        let question = this.surveyQuestions.find(q => q.id == +v);
        if (question && question.type !== 'narrative') {
          incomplete.push(+v)
        }
      }
    });

    // Set incomplete responses
    if (incomplete.length > 0) {
      this.incompleteResponses = true;
    } else {
      this.incompleteResponses = false;
    }

    // Add flag to survey questions
    if (check) {
      this.surveyQuestions.forEach(q => {
        if (incomplete.includes(q.id) && q.type !== 'narrative') {
          q.incomplete = true;
        } else {
          q.incomplete = false;
        }
      });
    }

  }

}
